import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Skeleton } from '../Skeleton';
import { formatQuantity } from '@fewerbags/core';
import { Alert } from '../Alert';
import { SelectField } from '../SelectField';
import { api } from '../../lib/api';
import { ANY_STORE, defaultStoreOptions, defaultsForStore } from '../../lib/defaultProducts';
import { messageForError } from '../../lib/errors';
import { storeQueryKeys } from '../../pages/stores/queryKeys';

/**
 * The household's default product per ingredient (drives package rounding on
 * grocery lists, §4.3, and the product a check-off assumes). Defaults are per
 * store, with an "Any store" default as the fallback; check-offs learn them
 * (what was actually bought at that store). This is a read + clear list,
 * filtered by the store selector.
 */
export function DefaultProductsSection() {
  const qc = useQueryClient();
  const [store, setStore] = useState(ANY_STORE);
  const defaults = useQuery({
    queryKey: storeQueryKeys.defaults,
    queryFn: () => api.stores.products.defaults(),
  });

  const clear = useMutation({
    mutationFn: (args: { ingredientId: string; storeId: string | null }) =>
      api.stores.products.setDefault({ ...args, productId: null }),
    onSuccess: () => qc.invalidateQueries({ queryKey: storeQueryKeys.defaults }),
  });

  const all = defaults.data?.defaults ?? [];
  const options = defaultStoreOptions(all);
  // A store whose last default was cleared drops out of the options.
  const selected = options.some((o) => o.value === store) ? store : ANY_STORE;
  const shown = defaultsForStore(all, selected);

  return (
    <div className="flex flex-col gap-3">
      {defaults.isPending ? <Skeleton lines={3} /> : null}
      {defaults.isError ? <Alert tone="error">{messageForError(defaults.error)}</Alert> : null}
      {clear.isError ? <Alert tone="error">{messageForError(clear.error)}</Alert> : null}
      {defaults.isSuccess && all.length === 0 ? (
        <p className="text-sm text-muted">
          No default products yet — these are set while checking off a grocery list.
        </p>
      ) : null}
      {all.length > 0 ? (
        <SelectField
          id="default-products-store"
          label="Store"
          options={options}
          value={selected}
          onChange={(e) => setStore(e.target.value)}
        />
      ) : null}
      {all.length > 0 && shown.length === 0 ? (
        <p className="text-sm text-muted">No defaults for this store.</p>
      ) : null}
      {shown.length > 0 ? (
        <ul className="flex flex-col divide-y divide-line rounded-lg border border-line">
          {shown.map(({ ingredientId, storeId, product }) => (
            <li
              key={`${ingredientId}:${storeId ?? ANY_STORE}`}
              className="flex items-center justify-between gap-2 px-3 py-2"
            >
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-ink">{product.ingredientName}</p>
                <p className="truncate text-xs text-muted">
                  {product.label} · {formatQuantity(product.packageQty, product.packageUnit)}
                  {product.brand ? ` · ${product.brand}` : ''}
                </p>
              </div>
              <button
                type="button"
                onClick={() => clear.mutate({ ingredientId, storeId })}
                disabled={clear.isPending}
                className="min-h-9 shrink-0 rounded-lg border border-line px-3 text-sm text-fail"
              >
                Clear
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
